import React, { useState, useEffect } from 'react';
import bodegaService from '../services/bodegaService';
import './PostDetailModal.css';

const SolicitudRecursosModal = ({ tarea, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [bodegas, setBodegas] = useState([]);
  const [selectedBodega, setSelectedBodega] = useState('');
  const [recursos, setRecursos] = useState([]);
  const [selectedRecursos, setSelectedRecursos] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [tipoSolicitud, setTipoSolicitud] = useState(null); // 'bodega' o 'compra'

  useEffect(() => {
    loadBodegas();
    loadRecursosDisponibles();
  }, []);

  const loadBodegas = async () => {
    try {
      const response = await bodegaService.getBodegas();
      setBodegas(response.data.results || response.data || []);
    } catch (error) {
      console.error('Error loading bodegas:', error);
    }
  };

  const loadRecursosDisponibles = async () => {
    // Obtener los recursos del presupuesto de la tarea
    if (tarea.presupuesto_costo_recursos_detalle) {
      const recursosConStock = tarea.presupuesto_costo_recursos_detalle.map(recurso => ({
        ...recurso,
        cantidad_solicitada: 1,
        tiene_stock: recurso.stock_info && parseFloat(recurso.stock_info.total_disponible) > 0
      }));
      setRecursos(recursosConStock);
    }
  };

  const toggleRecurso = (recurso) => {
    const index = selectedRecursos.findIndex(r => r.id === recurso.id);
    if (index >= 0) {
      // Remover
      setSelectedRecursos(selectedRecursos.filter(r => r.id !== recurso.id));
    } else {
      // Agregar
      setSelectedRecursos([...selectedRecursos, { ...recurso, cantidad_solicitada: 1 }]);
    }
  };

  const updateCantidad = (recursoId, cantidad) => {
    setSelectedRecursos(selectedRecursos.map(r =>
      r.id === recursoId ? { ...r, cantidad_solicitada: parseFloat(cantidad) || 0 } : r
    ));
  };

  const determinarTipoSolicitud = () => {
    // Si TODOS los recursos seleccionados tienen stock suficiente, es solicitud de bodega
    const todosTienenStock = selectedRecursos.every(r => {
      if (!r.stock_info) return false;
      const disponible = parseFloat(r.stock_info.stocks_por_bodega?.find(s => s.bodega_id === parseInt(selectedBodega))?.cantidad_disponible || 0);
      return disponible >= r.cantidad_solicitada;
    });

    return todosTienenStock ? 'bodega' : 'compra';
  };

  const handleSubmit = async () => {
    // La bodega es requerida para AMBOS tipos de solicitud
    if (!selectedBodega) {
      alert('Por favor selecciona una bodega');
      return;
    }

    if (selectedRecursos.length === 0) {
      alert('Por favor selecciona al menos un recurso');
      return;
    }

    const tipo = determinarTipoSolicitud();
    setTipoSolicitud(tipo);

    try {
      setLoading(true);

      if (tipo === 'bodega') {
        // Crear solicitud de bodega (salida de bodega)
        const data = {
          tarea_evento: tarea.id,
          bodega: parseInt(selectedBodega),
          observaciones_solicitud: observaciones,
          detalles_data: selectedRecursos.map(r => ({
            recurso: r.recurso_referencia_data?.id,
            presupuesto_costo_recurso: r.id,
            cantidad_solicitada: r.cantidad_solicitada,
            observaciones: ''
          }))
        };

        await bodegaService.createSolicitudBodega(data);
        alert('Solicitud de bodega creada exitosamente');
      } else {
        // Crear solicitud de recurso (compra)
        const data = {
          tarea_evento: tarea.id,
          bodega: parseInt(selectedBodega), // Bodega de recepción
          observaciones_solicitud: observaciones,
          detalles_data: selectedRecursos.map(r => ({
            recurso: r.recurso_referencia_data?.id,
            presupuesto_costo_recurso: r.id,
            cantidad_solicitada: r.cantidad_solicitada,
            proveedor_sugerido: null,
            observaciones: ''
          }))
        };

        await bodegaService.createSolicitudRecurso(data);
        alert('Solicitud de compra creada exitosamente');
      }

      if (onCreated) {
        onCreated();
      }
      onClose();
    } catch (error) {
      console.error('Error creating solicitud:', error);
      alert('Error al crear la solicitud: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Calcular si hay stock disponible en la bodega seleccionada
  const getStockEnBodega = (recurso) => {
    if (!selectedBodega || !recurso.stock_info) return null;
    const stock = recurso.stock_info.stocks_por_bodega?.find(s => s.bodega_id === parseInt(selectedBodega));
    return stock ? parseFloat(stock.cantidad_disponible) : 0;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header">
          <h2>📦 Solicitar Recursos para Tarea</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Info de la tarea */}
          <div style={{
            background: '#f3f4f6',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
              Tarea:
            </h4>
            <p style={{ margin: 0, fontWeight: '600' }}>{tarea.titulo}</p>
          </div>

          {/* Selección de bodega */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              Bodega {selectedRecursos.length > 0 && selectedBodega ?
                (determinarTipoSolicitud() === 'bodega' ? '(Origen - Salida)' : '(Destino - Recepción)')
                : ''} *
            </label>
            <select
              value={selectedBodega}
              onChange={(e) => setSelectedBodega(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}
            >
              <option value="">Selecciona una bodega...</option>
              {bodegas.map(bodega => (
                <option key={bodega.id} value={bodega.id}>
                  {bodega.nombre}
                </option>
              ))}
            </select>
            <div style={{
              fontSize: '0.75rem',
              color: '#6b7280',
              marginTop: '0.5rem'
            }}>
              {selectedRecursos.length > 0 && selectedBodega && determinarTipoSolicitud() === 'compra' && (
                <span>💡 Los recursos se recibirán en esta bodega cuando llegue la compra</span>
              )}
              {selectedRecursos.length > 0 && selectedBodega && determinarTipoSolicitud() === 'bodega' && (
                <span>💡 Los recursos saldrán de esta bodega</span>
              )}
            </div>
          </div>

          {/* Lista de recursos disponibles */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>
              Recursos Disponibles:
            </h4>

            {recursos.length === 0 ? (
              <div style={{
                background: '#fef3c7',
                padding: '1rem',
                borderRadius: '6px',
                fontSize: '0.875rem',
                color: '#92400e'
              }}>
                Esta tarea no tiene recursos asignados del presupuesto.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {recursos.map(recurso => {
                  const isSelected = selectedRecursos.some(r => r.id === recurso.id);
                  const stockEnBodega = getStockEnBodega(recurso);
                  const selectedRecurso = selectedRecursos.find(r => r.id === recurso.id);
                  const tieneStockSuficiente = stockEnBodega !== null && stockEnBodega >= (selectedRecurso?.cantidad_solicitada || 0);

                  return (
                    <div
                      key={recurso.id}
                      style={{
                        border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: isSelected ? '#eff6ff' : 'white',
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleRecurso(recurso)}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ marginTop: '0.25rem' }}
                        />

                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                            {recurso.nombre}
                          </div>

                          {recurso.recurso_referencia_data && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                              {recurso.recurso_referencia_data.nombre}
                            </div>
                          )}

                          {/* Mostrar stock si la bodega está seleccionada */}
                          {selectedBodega && recurso.stock_info && (
                            <div style={{
                              display: 'flex',
                              gap: '1rem',
                              flexWrap: 'wrap',
                              marginTop: '0.5rem'
                            }}>
                              <div style={{
                                fontSize: '0.75rem',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                background: tieneStockSuficiente ? '#dcfce7' : '#fee2e2',
                                color: tieneStockSuficiente ? '#166534' : '#991b1b'
                              }}>
                                📦 Stock: {stockEnBodega || 0} disponibles
                              </div>
                            </div>
                          )}

                          {/* Input de cantidad */}
                          {isSelected && (
                            <div style={{ marginTop: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                              <label style={{
                                display: 'block',
                                fontSize: '0.75rem',
                                marginBottom: '0.25rem',
                                fontWeight: '600'
                              }}>
                                Cantidad a solicitar:
                              </label>
                              <input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={selectedRecurso?.cantidad_solicitada || 1}
                                onChange={(e) => updateCantidad(recurso.id, e.target.value)}
                                style={{
                                  width: '150px',
                                  padding: '0.5rem',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontSize: '0.875rem'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              Observaciones (opcional)
            </label>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                resize: 'vertical'
              }}
              placeholder="Escribe observaciones adicionales..."
            />
          </div>

          {/* Info del tipo de solicitud que se creará */}
          {selectedRecursos.length > 0 && selectedBodega && (
            <div style={{
              background: determinarTipoSolicitud() === 'bodega' ? '#dcfce7' : '#fef3c7',
              color: determinarTipoSolicitud() === 'bodega' ? '#166534' : '#92400e',
              padding: '1rem',
              borderRadius: '6px',
              fontSize: '0.875rem',
              marginBottom: '1.5rem'
            }}>
              {determinarTipoSolicitud() === 'bodega' ? (
                <div>
                  <strong>✓ Solicitud de Bodega</strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    Se creará una solicitud de salida de bodega. Los recursos serán entregados desde la bodega seleccionada.
                  </p>
                </div>
              ) : (
                <div>
                  <strong>🛒 Solicitud de Compra</strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    No hay stock suficiente. Se creará una solicitud de compra para adquirir los recursos faltantes.
                    Los recursos se recibirán en la bodega seleccionada.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                border: '1px solid #d1d5db',
                background: 'white',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || selectedRecursos.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                background: loading || selectedRecursos.length === 0 ? '#9ca3af' : '#102826',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading || selectedRecursos.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}
            >
              {loading ? 'Creando...' : 'Crear Solicitud'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolicitudRecursosModal;
